// Material Takeoff · Editor page.
//
// End-to-end contractor surface for Intelligent Material Planning:
//   * Entry path picker (from_project / from_measurements / manual) when
//     no takeoff exists.
//   * List of items with source labels, alternatives, waste / contingency
//     / recoverable surplus visible per line.
//   * Draft controls: Save Draft, Run Double Check, Approve, Revise.
//   * Double Check surfaces findings but never mutates the takeoff.
//   * Missing prices remain visibly missing — never fabricated.
//
// The engine + intelligence live server-side. This page is a thin
// controller: contractor edits, Levi recommends, contractor decides.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useLeviWorkspaceHint } from "@/lib/leviWorkspace";
import {
  Package, Plus, Trash2, ClipboardCheck, RefreshCw, AlertTriangle,
  ArrowLeft, Loader2, PencilLine, ShieldQuestion, PackagePlus,
  Ruler, Wrench, Sparkles, ClipboardList,
} from "lucide-react";

const ENTRY_PATHS = ["from_project", "from_measurements", "manual"];
const SOURCE_KEYS = ["price_book", "project_scope", "levi_suggestion",
                     "contractor_added", "price_missing"];

const SourceBadge = ({ src, t }) => {
  const tone = {
    price_book: "bg-emerald-50 border-emerald-200 text-emerald-800",
    project_scope: "bg-sky-50 border-sky-200 text-sky-800",
    levi_suggestion: "bg-indigo-50 border-indigo-200 text-indigo-800",
    contractor_added: "bg-amber-50 border-amber-200 text-amber-800",
    price_missing: "bg-rose-50 border-rose-200 text-rose-800",
  }[src] || "bg-[var(--jc-bg)] border-[var(--jc-border)] text-[var(--jc-text-soft)]";
  return (
    <span
      data-testid={`source-badge-${src}`}
      className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${tone}`}
    >
      {t(`material_takeoff.source.${src}`)}
    </span>
  );
};

const StatusPill = ({ status, version, t }) => (
  <span
    data-testid="takeoff-status-pill"
    className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
      status === "approved"
        ? "bg-emerald-50 border-emerald-300 text-emerald-800"
        : "bg-amber-50 border-amber-300 text-amber-800"
    }`}
  >
    {t(`material_takeoff.status.${status}`)} · v{version}
  </span>
);

const fmtMoney = (v, lang) => {
  if (v == null) return "";
  const locale = lang === "es" ? "es-ES" : "en-US";
  try {
    return v.toLocaleString(locale, { style: "currency", currency: "USD" });
  } catch { return `$${v}`; }
};

const MaterialTakeoff = () => {
  const { t, lang } = useLang();
  const { pid } = useParams();
  // Phase 5A · Publish current-page workspace hint (backend
  // re-verifies the ID against the authenticated tenant).
  useLeviWorkspaceHint("material_takeoff", {
    active_project_id: pid,
    current_section: "material_takeoff",
  });
  const [takeoffs, setTakeoffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [runningDc, setRunningDc] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${pid}/material-takeoffs`);
      setTakeoffs(r.data || []);
    } catch (_) { setTakeoffs([]); }
    setLoading(false);
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const current = useMemo(() => {
    if (!takeoffs?.length) return null;
    return [...takeoffs].sort((a, b) => (b.version || 0) - (a.version || 0))[0];
  }, [takeoffs]);

  const isApproved = current?.status === "approved";
  const draftLocked = isApproved;

  const startTakeoff = async (entry_path, extras = {}) => {
    setSaving(true);
    try {
      const body = { entry_path, ...extras };
      const r = await api.post(`/projects/${pid}/material-takeoffs`, body);
      setPickerOpen(false);
      setTakeoffs((rows) => [r.data, ...rows]);
      toast.success(t("material_takeoff.toast_created"));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error
        || err?.response?.data?.detail
        || t("material_takeoff.toast_create_failed"));
    } finally { setSaving(false); }
  };

  const updateDraft = async (patch) => {
    if (!current || isApproved) return;
    setSaving(true);
    try {
      const r = await api.put(`/material-takeoffs/${current.id}`, patch);
      setTakeoffs((rows) => rows.map((x) => x.id === r.data.id ? r.data : x));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error
        || err?.response?.data?.detail
        || t("material_takeoff.toast_save_failed"));
    } finally { setSaving(false); }
  };

  const saveDraft = () => {
    if (!current) return;
    updateDraft({ trade: current.trade || "", system_type: current.system_type || "",
                  notes: current.notes || "", items: current.items || [] })
      .then(() => toast.success(t("material_takeoff.toast_saved")));
  };

  const runDoubleCheck = async () => {
    if (!current) return;
    setRunningDc(true);
    try {
      const r = await api.post(`/material-takeoffs/${current.id}/double-check`);
      setTakeoffs((rows) => rows.map((x) =>
        x.id === current.id ? { ...x, double_check: r.data } : x));
      const counts = r.data.counts || {};
      const total = (counts.info || 0) + (counts.warn || 0) + (counts.red || 0);
      toast.success(t("material_takeoff.toast_dc_ran", total));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error
        || t("material_takeoff.toast_dc_failed"));
    } finally { setRunningDc(false); }
  };

  const approve = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const r = await api.post(`/material-takeoffs/${current.id}/approve`);
      setTakeoffs((rows) => rows.map((x) => x.id === r.data.id ? r.data : x));
      toast.success(t("material_takeoff.toast_approved"));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error
        || err?.response?.data?.detail?.message
        || t("material_takeoff.toast_approve_failed"));
    } finally { setSaving(false); }
  };

  const revise = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const r = await api.post(`/material-takeoffs/${current.id}/revise`);
      setTakeoffs((rows) => [r.data, ...rows]);
      toast.success(t("material_takeoff.toast_revised"));
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error
        || t("material_takeoff.toast_revise_failed"));
    } finally { setSaving(false); }
  };

  const updateItem = (idx, patch) => {
    if (draftLocked) return;
    setTakeoffs((rows) => rows.map((x) => {
      if (x.id !== current.id) return x;
      const items = (x.items || []).map((it, i) =>
        i === idx ? { ...it, ...patch } : it);
      return { ...x, items };
    }));
  };

  const addBlankItem = () => {
    if (draftLocked) return;
    setTakeoffs((rows) => rows.map((x) => {
      if (x.id !== current.id) return x;
      return {
        ...x,
        items: [...(x.items || []), {
          id: `local-${Date.now()}`,
          name: "", product: "", unit: "each",
          quantity_required: 0, waste_pct: 0, contingency_pct: 0,
          unit_price: null, sources: ["contractor_added"],
        }],
      };
    }));
  };

  const removeItem = (idx) => {
    if (draftLocked) return;
    setTakeoffs((rows) => rows.map((x) => {
      if (x.id !== current.id) return x;
      const items = (x.items || []).filter((_, i) => i !== idx);
      return { ...x, items };
    }));
  };

  return (
    <DashboardLayout
      title={t("material_takeoff.page_title")}
      subtitle={t("material_takeoff.page_subtitle")}
      actions={
        <Link
          to={`/app/projects/${pid}`}
          data-testid="material-takeoff-back-btn"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--jc-text-soft)] hover:text-[var(--jc-text)]"
        >
          <ArrowLeft size={14} /> {t("material_takeoff.back")}
        </Link>
      }
    >
      {loading ? (
        <div className="text-sm text-[var(--jc-text-soft)]" data-testid="material-takeoff-loading">
          {t("material_takeoff.loading")}
        </div>
      ) : !current ? (
        <div data-testid="entry-path-picker" className="max-w-2xl">
          <div className="jc-card p-6 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Package size={18} />
              <div className="font-display text-lg">{t("material_takeoff.picker_title")}</div>
            </div>
            <p className="text-sm text-[var(--jc-text-soft)] mb-5">
              {t("material_takeoff.picker_intro")}
            </p>
            <div className="grid gap-3 sm:grid-cols-1 md:grid-cols-3">
              <EntryPathButton
                icon={PackagePlus} testid="entry-path-from_project"
                title={t("material_takeoff.entry.from_project.title")}
                body={t("material_takeoff.entry.from_project.body")}
                onClick={() => startTakeoff("from_project")}
              />
              <EntryPathButton
                icon={Ruler} testid="entry-path-from_measurements"
                title={t("material_takeoff.entry.from_measurements.title")}
                body={t("material_takeoff.entry.from_measurements.body")}
                onClick={() => setPickerOpen(true)}
              />
              <EntryPathButton
                icon={Wrench} testid="entry-path-manual"
                title={t("material_takeoff.entry.manual.title")}
                body={t("material_takeoff.entry.manual.body")}
                onClick={() => startTakeoff("manual")}
              />
            </div>
            {pickerOpen && (
              <MeasurementsPrompt
                onCancel={() => setPickerOpen(false)}
                onSubmit={(hint) => startTakeoff("from_measurements", {
                  measurement_hint: hint,
                  trade: hint.trade || "roofing",
                  system_type: hint.system_type || "",
                })}
              />
            )}
          </div>
        </div>
      ) : (
        <div data-testid="takeoff-editor" className="max-w-4xl space-y-4">
          <div className="jc-card p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="overline mb-1">{t("material_takeoff.editor_overline")}</div>
              <div className="font-display text-lg" data-testid="takeoff-heading">
                {t(`material_takeoff.entry.${current.entry_path}.title`)}
                {current.trade ? ` · ${current.trade}` : ""}
                {current.system_type ? ` · ${current.system_type}` : ""}
              </div>
            </div>
            <StatusPill status={current.status} version={current.version || 1} t={t} />
          </div>

          {/* Controls */}
          <div className="flex flex-wrap gap-2">
            {!isApproved && (
              <>
                <button
                  onClick={saveDraft} disabled={saving}
                  data-testid="save-draft-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                >
                  {saving ? <Loader2 className="animate-spin" size={13} /> : <PencilLine size={13} />}
                  {t("material_takeoff.save_draft")}
                </button>
                <button
                  onClick={runDoubleCheck} disabled={runningDc}
                  data-testid="run-double-check-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
                >
                  {runningDc ? <Loader2 className="animate-spin" size={13} /> : <ShieldQuestion size={13} />}
                  {t("material_takeoff.run_double_check")}
                </button>
                <button
                  onClick={approve} disabled={saving}
                  data-testid="approve-takeoff-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md jc-btn-primary"
                >
                  <ClipboardCheck size={13} /> {t("material_takeoff.approve_takeoff")}
                </button>
              </>
            )}
            {isApproved && (
              <button
                onClick={revise} disabled={saving}
                data-testid="revise-takeoff-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
              >
                <RefreshCw size={13} /> {t("material_takeoff.revise_takeoff")}
              </button>
            )}
          </div>

          {/* Double Check panel */}
          {current.double_check?.findings?.length > 0 && (
            <div
              data-testid="double-check-panel"
              className="jc-card p-4 border-l-4 border-yellow-400"
            >
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={14} className="text-yellow-700" />
                <div className="font-semibold text-sm">
                  {t("material_takeoff.dc_heading")}
                </div>
                <span className="text-xs text-[var(--jc-text-soft)]">
                  {t("material_takeoff.dc_never_mutates")}
                </span>
              </div>
              <ul className="space-y-1 text-sm">
                {current.double_check.findings.map((f, i) => (
                  <li key={i} data-testid={`dc-finding-${f.kind}`}
                      className="flex items-start gap-2">
                    <span className={`mt-0.5 inline-block px-1.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                      f.severity === "red" ? "bg-rose-100 text-rose-800"
                      : f.severity === "warn" ? "bg-amber-100 text-amber-800"
                      : "bg-sky-100 text-sky-800"
                    }`}>{f.severity}</span>
                    <div>
                      <div className="font-medium">{f.title}</div>
                      <div className="text-xs text-[var(--jc-text-soft)]">{f.detail}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Items */}
          <div className="jc-card p-4 space-y-3" data-testid="takeoff-items">
            <div className="flex items-center justify-between">
              <div className="overline">{t("material_takeoff.items_heading")}</div>
              {!isApproved && (
                <button
                  onClick={addBlankItem}
                  data-testid="add-item-btn"
                  className="text-xs font-semibold text-[var(--jc-blue)] hover:underline inline-flex items-center gap-1"
                >
                  <Plus size={12} /> {t("material_takeoff.add_item")}
                </button>
              )}
            </div>
            {(current.items || []).length === 0 ? (
              current.entry_path === "from_project" ? (
                <BuildFromProjectGuidance
                  pid={pid}
                  onBuildFromMeasurements={(hint) => startTakeoff("from_measurements", {
                    measurement_hint: hint,
                    trade: hint.trade || "roofing",
                    system_type: hint.system_type || "",
                  })}
                  onStartManual={() => startTakeoff("manual")}
                  t={t}
                />
              ) : (
                <div className="text-sm text-[var(--jc-text-soft)]" data-testid="takeoff-items-empty">
                  {t("material_takeoff.items_empty")}
                </div>
              )
            ) : (
              (current.items || []).map((it, idx) => (
                <ItemRow
                  key={it.id || idx}
                  it={it}
                  idx={idx}
                  locked={draftLocked}
                  onChange={(patch) => updateItem(idx, patch)}
                  onRemove={() => removeItem(idx)}
                  t={t}
                  lang={lang}
                />
              ))
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

const EntryPathButton = ({ icon: Icon, title, body, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="text-left jc-card p-4 hover:border-[var(--jc-blue)]/60 hover:shadow-sm transition"
  >
    <div className="flex items-center gap-2 mb-1.5">
      <Icon size={16} className="text-[var(--jc-blue)]" />
      <div className="font-semibold text-sm">{title}</div>
    </div>
    <div className="text-xs text-[var(--jc-text-soft)]">{body}</div>
  </button>
);

const MeasurementsPrompt = ({ onCancel, onSubmit }) => {
  const { t } = useLang();
  const [area, setArea] = useState("");
  const [trade, setTrade] = useState("roofing");
  const [systemType, setSystemType] = useState("tpo");
  return (
    <div className="mt-4 rounded-md border border-[var(--jc-border-strong)] bg-[var(--jc-bg)] p-4"
         data-testid="measurements-prompt">
      <div className="text-sm font-semibold mb-2">
        {t("material_takeoff.measurements_prompt_title")}
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="text-xs">
          <div className="text-[var(--jc-text-soft)] mb-1">
            {t("material_takeoff.measurements_area_label")}
          </div>
          <input
            type="number" value={area} min="0" step="1"
            onChange={(e) => setArea(e.target.value)}
            data-testid="measurements-area-input"
            className="w-full px-2 py-1.5 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white"
          />
        </label>
        <label className="text-xs">
          <div className="text-[var(--jc-text-soft)] mb-1">
            {t("material_takeoff.measurements_trade_label")}
          </div>
          <select
            value={trade} onChange={(e) => setTrade(e.target.value)}
            data-testid="measurements-trade-select"
            className="w-full px-2 py-1.5 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white"
          >
            <option value="roofing">{t("material_takeoff.trade.roofing")}</option>
            <option value="drywall">{t("material_takeoff.trade.drywall")}</option>
            <option value="siding">{t("material_takeoff.trade.siding")}</option>
            <option value="flooring">{t("material_takeoff.trade.flooring")}</option>
          </select>
        </label>
        <label className="text-xs">
          <div className="text-[var(--jc-text-soft)] mb-1">
            {t("material_takeoff.measurements_system_label")}
          </div>
          <input
            type="text" value={systemType}
            onChange={(e) => setSystemType(e.target.value)}
            data-testid="measurements-system-input"
            className="w-full px-2 py-1.5 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white"
          />
        </label>
      </div>
      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={() => onSubmit({ area_sqft: Number(area) || 0, trade, system_type: systemType })}
          disabled={!area || Number(area) <= 0}
          data-testid="measurements-submit-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md jc-btn-primary disabled:opacity-60"
        >
          {t("material_takeoff.measurements_submit")}
        </button>
        <button
          type="button" onClick={onCancel}
          data-testid="measurements-cancel-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)]"
        >
          {t("material_takeoff.cancel")}
        </button>
      </div>
    </div>
  );
};

const ItemRow = ({ it, idx, locked, onChange, onRemove, t, lang }) => (
  <div
    data-testid={`item-row-${idx}`}
    className="rounded-md border border-[var(--jc-border)] p-3"
  >
    <div className="flex items-start justify-between gap-3 mb-2">
      <div className="min-w-0 flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input
          type="text" value={it.name || ""}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder={t("material_takeoff.field_name")}
          disabled={locked}
          data-testid={`item-name-${idx}`}
          className="px-2 py-1.5 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white font-semibold"
        />
        <input
          type="text" value={it.product || ""}
          onChange={(e) => onChange({ product: e.target.value })}
          placeholder={t("material_takeoff.field_product")}
          disabled={locked}
          data-testid={`item-product-${idx}`}
          className="px-2 py-1.5 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white"
        />
      </div>
      <div className="shrink-0 flex items-center gap-2">
        {!locked && (
          <button
            type="button" onClick={onRemove}
            data-testid={`item-remove-${idx}`}
            className="p-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50"
            title={t("material_takeoff.remove")}
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
    </div>

    <div className="flex flex-wrap gap-1 mb-2" data-testid={`item-sources-${idx}`}>
      {SOURCE_KEYS.filter((k) => (it.sources || []).includes(k)).map((s) => (
        <SourceBadge key={s} src={s} t={t} />
      ))}
    </div>

    <div className="grid gap-2 sm:grid-cols-4">
      <NumField label={t("material_takeoff.field_qty")} value={it.quantity_required}
                onChange={(v) => onChange({ quantity_required: v })}
                disabled={locked} testid={`item-qty-${idx}`} />
      <NumField label={t("material_takeoff.field_waste_pct")} value={it.waste_pct}
                onChange={(v) => onChange({ waste_pct: v })}
                disabled={locked} testid={`item-waste-${idx}`} />
      <NumField label={t("material_takeoff.field_contingency_pct")} value={it.contingency_pct}
                onChange={(v) => onChange({ contingency_pct: v })}
                disabled={locked} testid={`item-contingency-${idx}`} />
      <NumField label={t("material_takeoff.field_unit_price")}
                value={it.unit_price ?? ""}
                onChange={(v) => onChange({ unit_price: v === "" ? null : v })}
                disabled={locked} allowEmpty testid={`item-price-${idx}`} />
    </div>

    <div className="mt-2 grid gap-2 sm:grid-cols-4 text-xs">
      <ReadonlyCell label={t("material_takeoff.calc_required_waste")}
                    value={it.required_waste_qty ?? 0} testid={`item-req-waste-${idx}`} />
      <ReadonlyCell label={t("material_takeoff.calc_contingency")}
                    value={it.contingency_qty ?? 0} testid={`item-conting-${idx}`} />
      <ReadonlyCell label={t("material_takeoff.calc_surplus")}
                    value={it.recoverable_surplus_qty ?? 0} testid={`item-surplus-${idx}`} />
      <ReadonlyCell label={t("material_takeoff.calc_total")}
                    value={it.total_qty ?? 0} suffix={` ${it.unit || ""}`}
                    testid={`item-total-${idx}`} />
    </div>

    {it.price_missing ? (
      <div className="text-xs text-rose-800 mt-2" data-testid={`item-price-missing-${idx}`}>
        {t("material_takeoff.price_missing_help")}
      </div>
    ) : (
      it.total_price != null && (
        <div className="text-xs mt-2" data-testid={`item-total-price-${idx}`}>
          <span className="text-[var(--jc-text-soft)]">
            {t("material_takeoff.calc_line_total")}
          </span>{" "}
          <span className="font-semibold">{fmtMoney(it.total_price, lang)}</span>
        </div>
      )
    )}

    {it.recommendation_reason && (
      <div className="text-xs text-[var(--jc-text-soft)] mt-2"
           data-testid={`item-reason-${idx}`}>
        “{it.recommendation_reason}”
      </div>
    )}

    {(it.alternatives || []).length > 0 && (
      <details className="mt-2" data-testid={`item-alternatives-${idx}`}>
        <summary className="text-xs font-semibold cursor-pointer text-[var(--jc-blue)]">
          {t("material_takeoff.alternatives_toggle", (it.alternatives || []).length)}
        </summary>
        <ul className="mt-1 space-y-1 text-xs">
          {it.alternatives.map((a, i) => (
            <li key={i} className="border-l-2 border-[var(--jc-border)] pl-2">
              <div className="font-medium">{a.description || `${t("material_takeoff.alternative")} ${i + 1}`}</div>
              <div className="text-[var(--jc-text-soft)]">{a.tradeoff}</div>
            </li>
          ))}
        </ul>
      </details>
    )}
  </div>
);

const NumField = ({ label, value, onChange, disabled, allowEmpty, testid }) => (
  <label className="text-xs">
    <div className="text-[var(--jc-text-soft)] mb-1">{label}</div>
    <input
      type="number" step="0.01" min="0" disabled={disabled}
      value={value ?? (allowEmpty ? "" : 0)}
      onChange={(e) => {
        const v = e.target.value;
        if (allowEmpty && v === "") { onChange(""); return; }
        onChange(v === "" ? 0 : Number(v));
      }}
      data-testid={testid}
      className="w-full px-2 py-1.5 rounded-md border border-[var(--jc-border-strong)] text-sm bg-white disabled:bg-[var(--jc-bg)]"
    />
  </label>
);

const ReadonlyCell = ({ label, value, suffix = "", testid }) => (
  <div>
    <div className="text-[var(--jc-text-soft)]">{label}</div>
    <div className="font-semibold" data-testid={testid}>
      {typeof value === "number" ? value.toFixed(2) : value}{suffix}
    </div>
  </div>
);

// Levi in-page guidance shown ONLY when Build from Project produced an
// empty draft — i.e. the project didn't yet have organized scope or
// priced estimate line items. The engineering is correct; this panel
// tells the contractor WHY and offers the three existing next steps.
const BuildFromProjectGuidance = ({
  pid, onBuildFromMeasurements, onStartManual, t,
}) => {
  const [promptOpen, setPromptOpen] = useState(false);
  return (
    <div
      data-testid="build-from-project-guidance"
      className="rounded-md border border-indigo-200 bg-indigo-50 p-4"
    >
      <div className="flex items-start gap-2 mb-2">
        <Sparkles size={16} className="mt-0.5 shrink-0 text-indigo-700" />
        <div className="min-w-0">
          <div className="font-semibold text-sm text-indigo-900"
               data-testid="build-from-project-guidance-heading">
            {t("material_takeoff.guidance_from_project.heading")}
          </div>
          <div className="text-xs text-indigo-900/80 mt-1 whitespace-pre-line"
               data-testid="build-from-project-guidance-body">
            {t("material_takeoff.guidance_from_project.body")}
          </div>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-3 mt-3">
        <Link
          to={`/app/projects/${pid}`}
          data-testid="guidance-continue-estimate-btn"
          className="text-left text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] bg-white px-3 py-2 hover:border-[var(--jc-blue)]/60 inline-flex items-center gap-2"
        >
          <ClipboardList size={14} className="text-[var(--jc-blue)]" />
          {t("material_takeoff.guidance_from_project.action_continue_estimate")}
        </Link>
        <button
          type="button"
          onClick={() => setPromptOpen(true)}
          data-testid="guidance-build-from-measurements-btn"
          className="text-left text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] bg-white px-3 py-2 hover:border-[var(--jc-blue)]/60 inline-flex items-center gap-2"
        >
          <Ruler size={14} className="text-[var(--jc-blue)]" />
          {t("material_takeoff.guidance_from_project.action_build_from_measurements")}
        </button>
        <button
          type="button"
          onClick={onStartManual}
          data-testid="guidance-start-manual-btn"
          className="text-left text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] bg-white px-3 py-2 hover:border-[var(--jc-blue)]/60 inline-flex items-center gap-2"
        >
          <Wrench size={14} className="text-[var(--jc-blue)]" />
          {t("material_takeoff.guidance_from_project.action_start_manual")}
        </button>
      </div>

      {promptOpen && (
        <MeasurementsPrompt
          onCancel={() => setPromptOpen(false)}
          onSubmit={(hint) => { setPromptOpen(false); onBuildFromMeasurements(hint); }}
        />
      )}
    </div>
  );
};

export default MaterialTakeoff;
